(ns manage.ad-variant-insights.view
  (:require ["@mui/material/Avatar$default" :as Avatar]
            ["@mui/material/Card$default" :as Card]
            ["@mui/material/CardContent$default" :as CardContent]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/LinearProgress$default" :as LinearProgress]
            ["@mui/material/List$default" :as List]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/ListItemAvatar$default" :as ListItemAvatar]
            ["@mui/material/ListItemText$default" :as ListItemText]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["src/svg/arrows/arrow-trend-up.svg$default" :as ArrowTrendUp]
            ["src/svg/artwork/data-viz.svg$default" :as DataVizArtwork]
            ["src/svg/inform/lightbulb-fill.svg$default" :as LightbulbFill]
            [common.patterns.empty-state-view :refer [empty-state-view]]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset-q]
            [common.util :as util]
            [manage.ad-variant-insights.charts :as charts]
            [manage.ad-variant-insights.queries :as q]
            [manage.ad-variant-insights.tables.ad-variants :refer [ad-variant-rankings-table]]
            [manage.ad-variant-insights.tables.decision-paths :refer [decision-path-rankings-table]]
            [uix.core :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui metric-card
  [{:keys [label value value-icon sublabel progress-value no-data?]}]
  ($ Card {:sx #js {:flex 1
                    :display "flex"
                    :flexDirection "column"}}
     ($ CardContent
        {:component Stack
         :gap 0.25
         :sx #js {:flex 1
                  :px 5
                  :py "21px !important"}}

        ($ Typography
           {:variant "h4"
            :sx #js {:color "grey.20"
                     :fontWeight 500}}
           label)
        ($ Stack
           {:direction "row"
            :gap 0.5
            :sx #js {:alignItems "center"}}
           ($ Typography
              {:variant "h1"
               :sx #js {:fontWeight 600
                        :color (if no-data? "grey.20" "inherit")}}
              value)
           value-icon)
        (when (some? progress-value)
          ($ LinearProgress
             {:variant "determinate"
              :value progress-value
              :sx #js {:height 6
                       :borderRadius 1
                       :backgroundColor "grey.70"
                       ".MuiLinearProgress-bar" #js {:backgroundColor "primary.400"}}}))
        (when sublabel
          ($ Typography
             {:variant "caption"
              :sx #js {:color "grey.20"
                       :lineHeight "20px"}}
             sublabel)))))

(defui insights-summary-row
  [{:keys [summary-metrics adset-id optimized?]}]
  (let [{{:keys [targetImpressions plannedStart plannedEnd]} :data} (use-query (adset-q/adset-query {:adset-id adset-id}))
        {{:keys [clicks impressions]} :data} (use-query (q/adset-stats {:adset-id adset-id
                                                                        :start-date plannedStart
                                                                        :end-date plannedEnd}))
        {:keys [click-uplift-percentage optimization-score recommendations-count]} summary-metrics]
    ($ Stack
       {:direction "row"
        :spacing 2}
       ($ metric-card
          {:label "Total impressions"
           :value (util/->locale-string impressions)
           :value-icon ($ SvgIcon ($ ArrowTrendUp))
           :sublabel (when targetImpressions
                       (str "of " (util/->locale-string targetImpressions) " target"))})
       ($ metric-card
          {:label "CTR"
           :value (if (or (zero? clicks) (zero? impressions))
                    0
                    (util/->locale-string (/ clicks impressions) {:style "decimal"
                                                                  :maximumFractionDigits 3}))
           :value-icon ($ SvgIcon ($ ArrowTrendUp))
           :sublabel "Average CTR"})
       ($ metric-card
          {:label "Click uplift"
           :value (if optimized?
                    (when click-uplift-percentage
                      (str "+" (util/->locale-string (/ click-uplift-percentage 100) {:style "percent"
                                                                                      :maximumFractionDigits 2})))
                    "No data")
           :no-data? (not optimized?)
           :sublabel (if optimized?
                       "vs Control group"
                       "Creative optimization required")})
       ($ metric-card
          {:label "Optimization score"
           :value (if optimized?
                    (when optimization-score
                      (str optimization-score "%"))
                    "No data")
           :no-data? (not optimized?)
           :sublabel (if optimized?
                       (when recommendations-count
                         (str recommendations-count " recommendations"))
                       "Creative optimization required")
           :progress-value (if optimized? optimization-score 0)}))))

(defui recommendation-item
  [{:keys [title description impact-percentage]}]
  ($ ListItem
     {:sx #js {:backgroundColor "grey.90"
               :borderRadius "6px"
               :py 1.5
               :px 2
               :gap 1}}
     ($ ListItemAvatar {:sx #js {:minWidth "auto"}}
        ($ Avatar
           {:sx #js {:width 32
                     :height 32
                     :backgroundColor "primary.100"}}
           ($ SvgIcon
              {:sx #js {:width 20
                        :height 20
                        :color "primary.main"}}
              ($ LightbulbFill))))
     ($ ListItemText
        {:primary ($ Typography
                     {:variant "body2"
                      :sx #js {:color "grey.100"
                               :fontWeight 400
                               :fontSize "14px"
                               :lineHeight "22px"}}
                     title)
         :secondary ($ Typography
                       {:variant "body2"
                        :sx #js {:color "grey.20"
                                 :fontSize "14px"
                                 :lineHeight "22px"}}
                       description)
         :sx #js {:m 0}})
     ($ Chip
        {:sx #js {:minWidth 48}
         :size "small"
         :variant "subtle"
         :color "success"
         :label ($ Typography
                   {:sx #js {:fontSize "12px"
                             :fontWeight 500
                             :lineHeight "20px"}}
                   (str "+" impact-percentage "%"))})))

(defui optimization-recommendations-list
  [{:keys [recommendations]}]
  ($ Stack
     {:component List
      :direction "column"
      :gap 1
      :sx #js {:p 0}}
     (for [{:keys [id title description impact-percentage]} recommendations]
       ($ recommendation-item
          {:key id
           :title title
           :description description
           :impact-percentage impact-percentage}))))

(defui optimization-recommendations-row
  [{:keys [recommendations]}]
  ($ Card
     ($ CardContent
        {:sx #js {:p 4
                  :pt 3}}
        ($ Typography
           {:variant "h2"
            :sx #js {:mb 1.5
                     :fontWeight 600
                     :lineHeight "3.2rem"}}
           "Optimization Recommendations")
        ($ optimization-recommendations-list
           {:recommendations recommendations}))))

(defui insights-charts-row
  [{:keys [adset-id optimized?]}]
  (let [{{:keys [plannedStart plannedEnd]} :data} (use-query (adset-q/adset-query {:adset-id adset-id}))
        {:keys [data]} (use-query (q/ctr-per-day-query {:adset-id adset-id
                                                        :start-date plannedStart
                                                        :end-date plannedEnd}))
        {{:keys [clicks impressions]} :data} (use-query (q/adset-stats {:adset-id adset-id
                                                                        :start-date plannedStart
                                                                        :end-date plannedEnd}))]
    ($ Stack
       {:direction "row"
        :spacing 3}
       ($ Card
          {:sx #js {:flex 3
                    :minHeight 460}}
          ($ CardContent
             {:sx #js {:p 4
                       :pt 3}}
             ($ Typography {:variant "h2"
                            :sx #js {:fontWeight 600
                                     :lineHeight "3.2rem"}}
                "CTR Performance Trend")
             (if (seq data)
               ($ charts/ctr-performance-trend-chart {:time-series data
                                                      :optimized? optimized?})
               ($ empty-state-view
                  {:artwork DataVizArtwork
                   :sx #js {:height "100%"}
                   :title "No available data yet"}))))
       ($ Card
          {:sx #js {:flex 1}}
          ($ CardContent
             {:sx #js {:p 3
                       :pt 2.5}}
             ($ Typography
                {:variant "h2"
                 :sx #js {:fontWeight 600
                          :lineHeight "3.2rem"}}
                "CTR")
             (if (or (nil? clicks)
                     (nil? impressions)
                     (zero? clicks)
                     (zero? impressions))
               ($ empty-state-view
                  {:artwork DataVizArtwork
                   :sx #js {:height "100%"}
                   :title "No available data yet"})
               ($ charts/ctr-comparison-chart {:optimized? optimized?
                                               :ctr (/ clicks impressions)})))))))

(defui ad-variant-rankings-card []
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{:keys [plannedStart plannedEnd]} :data} (use-query (adset-q/adset-query {:adset-id adset-id}))
        {stats :data} (use-query (q/ad-variant-stats {:adset-id adset-id
                                                      :start-date plannedStart
                                                      :end-date plannedEnd}))]
    ($ Card
       {:sx #js {:minHeight 423}}
       ($ CardContent
          {:sx #js {:p 4
                    :pt 3}}
          ($ Stack
             {:direction "column"
              :spacing 1.5
              :sx #js {:mb 2}}
             ($ Typography
                {:variant "h2"
                 :sx #js {:fontWeight 600
                          :lineHeight "3.2rem"}}
                "Ad Ranking")
             ($ ad-variant-rankings-table
                {:adset-id adset-id
                 :advertiser-id advertiser-id}))))))

(defui decision-path-rankings-card []
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {{:keys [plannedStart plannedEnd]} :data} (use-query (adset-q/adset-query {:adset-id adset-id}))
        {paths-insights :data} (use-query (q/decision-tree-paths-stats {:adset-id adset-id
                                                                        :start-date plannedStart
                                                                        :end-date plannedEnd}))]
    ($ Card {:sx #js {:minHeight 544}}
       ($ CardContent
          {:sx #js {:p 4
                    :pt 3}}
          ($ Stack
             {:spacing 1.5
              :sx #js {:mb 2}}
             ($ Typography
                {:variant "h2"
                 :sx #js {:fontWeight 600
                          :lineHeight "3.2rem"}}
                "Ranking by decision path"))
          ($ decision-path-rankings-table {:paths paths-insights})))))

(defui insights-tab []
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])

        {insights :data} (use-query (q/adset-insights-query {:advertiser-id advertiser-id
                                                             :adset-id adset-id}))
        {{:keys [test-state]} :data} (use-query (adset-q/adset-aco-active-configuration
                                                 {:advertiser-id advertiser-id
                                                  :adset-id adset-id}))
        optimized? (= test-state "started")]
    ($ Stack
       {:sx #js {:py 3
                 :gap 3}}
       (when insights
         ($ :<>
            ($ insights-summary-row
               {:summary-metrics (:summary-metrics insights)
                :optimized? optimized?
                :adset-id adset-id})
            ($ insights-charts-row
               {:adset-id adset-id
                :optimized? optimized?})
            (when optimized?
              (when-some [recommendations (seq (:optimization-recommendations insights))]
                ($ optimization-recommendations-row
                   {:recommendations recommendations})))
            ($ ad-variant-rankings-card)
            ($ decision-path-rankings-card))))))

